import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const PricingPage = () => {
  const navigate = useNavigate();
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });
  const BASEURL = process.env.REACT_APP_BASEURL;

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDemoSubmit = async(e) => {
    e.preventDefault();
    console.log("Demo request:", formData);
    try {
      const res = await fetch(`${BASEURL}/api/WebsiteEmailApi/SendContact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Name:formData.name,
          Phone:formData.phone,
          Email:formData.email,
          Company:formData.company,
          Message:formData.message
        })
      });
      if (res.status) {
        toast.success("Demo request submitted successfully! We'll contact you soon.");
        setDemoDialogOpen(false);
        setFormData({ name: "", email: "", phone: "", company: "", message: "" });
      }
    } catch (error) {
      console.log(error.stack);
      toast.warning("Error booking a demo. Please try again");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center">
              <a href="/" data-testid="logo">
                <img 
                  src="https://customer-assets.emergentagent.com/job_fintech-demo-9/artifacts/hrowc68u_image.png" 
                  alt="TurboPay" 
                  className="h-8 md:h-10 w-auto"
                />
              </a>
            </div>
            <div className="hidden lg:flex items-center space-x-8">
              <a href="/" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors">Home</a>
              {/* <a href="/pricing" className="text-[#1F497D] hover:text-[#183a64] font-medium transition-colors">Pricing</a> */}
              {/* <a href="/faq" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors">FAQs</a> */}
              <Link to="/pricing" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-pricing">
                Pricing
              </Link>
              <Link to="/faq" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-pricing">
                FAQs
              </Link>
            </div>
            <div className="hidden lg:block">
              <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1F497D] text-white hover:bg-[#183a64] h-11 px-8 rounded-full font-semibold shadow-[0_4px_14px_0_rgba(31,73,125,0.3)] transition-all hover:scale-105 active:scale-95">
                    Get Demo
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle className="font-outfit text-2xl">Book a Demo</DialogTitle>
                    <DialogDescription>
                      Fill out the form below and we'll get back to you shortly.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleDemoSubmit} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input id="name" name="name" required value={formData.name} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="company">Company</Label>
                      <Input id="company" name="company" value={formData.company} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea id="message" name="message" rows={3} value={formData.message} onChange={handleFormChange} className="mt-1.5" />
                    </div>
                    <Button type="submit" className="w-full bg-[#1F497D] hover:bg-[#183a64] h-11 rounded-full font-semibold">
                      Book Demo
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </nav>

      {/* Pricing Intro */}
      <section className="py-8 md:py-8 bg-gradient-to-br from-blue-50 to-white">
        <div className="container-custom text-center">
          <h1 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-tight text-slate-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Choose the right plan for your business. Collections software subscription + working capital funding when you need it.
          </p>
        </div>
      </section>

      {/* Collections Subscription Plans */}
      <section className="py-8 md:py-8" data-testid="pricing-subscription-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 mb-3">Collections Software</h2>
            <p className="text-slate-600">Annual subscription plans for digital collections management</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* LITE */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300" data-testid="pricing-card-lite">
              <div className="mb-6">
                <h3 className="font-outfit text-2xl font-bold text-slate-900 mb-2">LITE</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-slate-900">₹1,999</span>
                  <span className="text-slate-600">/Month</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8">
                   <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>Maximum 1,000 Invoices Per Month</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>2 Smartphone and 1 Desktop Access</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>Beat-Wise Collection Management</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>Single Organisation</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>Cash Flow Projections</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>QR Code Based Collection</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>SMS Code verification of Cash Receipts</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>Balance Confirmation via SMS</span>
                </li>
                {/* <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>Maximum 300 Customers</span>
                </li> */}
              </ul>
              <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full h-11 rounded-full" data-testid="pricing-lite-btn">
                    Subscribe to Lite
                  </Button>
                </DialogTrigger>
              </Dialog>
            </div>

            {/* PROFESSIONAL */}
            <div className="bg-white border-2 border-[#1F497D] rounded-2xl p-8 shadow-[0_8px_24px_rgba(31,73,125,0.15)] hover:shadow-[0_12px_32px_rgba(31,73,125,0.2)] transition-all duration-300 relative" data-testid="pricing-card-professional">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#1F497D] text-white px-4 py-1 rounded-full text-sm font-semibold">
                Popular
              </div>
              <div className="mb-6">
                <h3 className="font-outfit text-2xl font-bold text-slate-900 mb-2">PROFESSIONAL</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-slate-900">₹2,999</span>
                  <span className="text-slate-600">/Month</span>
                </div>
              </div>
              <p className="text-sm font-medium text-slate-700 mb-4">Everything in Lite and…</p>
              <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>Maximum 2,000 Invoices Per Month</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>5 Smartphone and 1 Desktop Access</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>Multiple Organisations</span>
                </li>
                {/* <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>Sales and Collection Reports</span>
                </li> */}
                {/* <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>Maximum 500 Customers</span>
                </li> */}
              </ul>
              <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-[#1F497D] hover:bg-[#183a64] h-11 rounded-full" data-testid="pricing-professional-btn">
                    Subscribe to Professional
                  </Button>
                </DialogTrigger>
              </Dialog>
            </div>

            {/* ENTERPRISE */}
            <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300" data-testid="pricing-card-enterprise">
              <div className="mb-6">
                <h3 className="font-outfit text-2xl font-bold text-slate-900 mb-2">ENTERPRISE</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-slate-900">Custom Pricing</span>
                  {/* <span className="text-slate-600">/User/Year</span> */}
                </div>
              </div>
              <p className="text-sm font-medium text-slate-700 mb-4">Everything in Professional and …</p>
              <ul className="space-y-3 mb-8">
                {/* <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>Unlimited Invoices</span>
                </li> */}
                <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>Unlimited Users</span>
                </li>
                 <li className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-[#1F497D] flex-shrink-0 mt-0.5" />
                  <span>Unlimited Organisations</span>
                </li>
              </ul>
              <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full h-11 rounded-full" data-testid="pricing-enterprise-btn">
                    Subscribe to Enterprise
                  </Button>
                </DialogTrigger>
              </Dialog>
            </div>
          </div>

          <p className="text-center text-sm text-slate-600 mt-8 max-w-2xl mx-auto">
            <strong>Note:</strong> Prices exclusive of taxes. Integration charges may apply for non-standard ERPs.
          </p>
        </div>
      </section>

      {/* Working Capital Funding */}
      <section className="py-12 md:py-16 bg-slate-50" data-testid="pricing-funding-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 mb-3">Working Capital Funding</h2>
            <p className="text-slate-600">Access instant funding for your business needs — pay only for what you use</p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {/* Distributor Funding Card */}
            <div className="bg-white rounded-2xl p-8 border-2 border-blue-200 shadow-sm">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <ArrowRight className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-outfit text-2xl font-bold text-slate-900 mb-2">Distributor Funding</h3>
                  <p className="text-slate-600">Get Instant Credit against your pending Invoices</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-1">Fee Structure</p>
                  <p className="text-2xl font-bold text-blue-600">1.75% - 2.0%</p>
                  <p className="text-xs text-slate-500 mt-1">per month on funded amount</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-1">Processing Time</p>
                  <p className="text-2xl font-bold text-blue-600">Same Day</p>
                  <p className="text-xs text-slate-500 mt-1">funds disbursed in hours</p>
                </div>
              </div>

              {/* <div className="flex items-start gap-2 p-4 bg-blue-50 rounded-lg mb-6">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">
                  <strong>Example:</strong> For ₹1,00,000 funded for 30 days at 2% monthly rate = ₹2,000 fee
                </p>
              </div> */}

              <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 h-11 px-8 rounded-full font-semibold" data-testid="apply-distributor-funding-btn">
                    Apply for Distributor Funding
                  </Button>
                </DialogTrigger>
              </Dialog>
            </div>

            {/* Retailer Funding Card */}
            <div className="bg-white rounded-2xl p-8 border-2 border-green-200 shadow-sm">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-green-500 flex items-center justify-center flex-shrink-0">
                  <ArrowRight className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-outfit text-2xl font-bold text-slate-900 mb-2">Retailer Funding</h3>
                  <p className="text-slate-600">Get Purchase Invoice Funding without collateral</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-1">Fee Structure</p>
                  <p className="text-2xl font-bold text-green-600">2% - 2.5%</p>
                  <p className="text-xs text-slate-500 mt-1">per month on funded amount</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-1">Credit Limit</p>
                  <p className="text-2xl font-bold text-green-600">Up to ₹1L</p>
                  <p className="text-xs text-slate-500 mt-1">based on purchase history</p>
                </div>
              </div>

              {/* <div className="flex items-start gap-2 p-4 bg-green-50 rounded-lg mb-6">
                <Info className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">
                  <strong>Example:</strong> For ₹50,000 credit for 30 days at 2.5% monthly rate = ₹1,250 fee
                </p>
              </div> */}

              <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full md:w-auto bg-green-600 hover:bg-green-700 h-11 px-8 rounded-full font-semibold" data-testid="apply-retailer-funding-btn">
                    Apply for Retailer Funding
                  </Button>
                </DialogTrigger>
              </Dialog>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-600 mb-6">Have questions about our pricing?</p>
            <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="h-12 px-8 rounded-full font-semibold" data-testid="pricing-contact-sales-btn">
                  Contact Sales
                </Button>
              </DialogTrigger>
            </Dialog>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12" data-testid="footer">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              {/* <a href="/" className="hover:text-[#1F497D] transition-colors">Home</a> */}
              <Link to="/" className="hover:text-[#1F497D] transition-colors">
                Home
              </Link>
              <Link to="/pricing" className="hover:text-[#1F497D] transition-colors">
                Pricing
              </Link>
              <Link to="/faq" className="hover:text-[#1F497D] transition-colors">
                FAQs
              </Link>
              {/* <a href="/faq" className="hover:text-[#1F497D] transition-colors">FAQs</a> */}
              <a href="#" className="hover:text-[#1F497D] transition-colors">Contact</a>
            </div>
            <p className="text-sm text-slate-400">© 2026 TurboPay. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};